import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";

export default function ForgotPassword() {
  const [identifier, setIdentifier] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [devUrl, setDevUrl] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { identifier: identifier.trim() });
      setSent(true);
      // dev_reset_url is only present in non-production builds — convenient for QA.
      if (data?.dev_reset_url) setDevUrl(data.dev_reset_url);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2 bg-white" data-testid="forgot-password-page">
      <div className="hidden lg:block relative">
        <img src="/hero-model.jpg" alt="" className="absolute inset-0 w-full h-full object-cover object-[center_20%]" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <h2 className="font-display text-7xl tracking-tighter leading-[0.85]">FORGOT IT?<br /><span className="text-[#FF3B30]">RESET IT.</span></h2>
          <p className="text-white/80 mt-6 max-w-md">Enter your email or mobile and we&apos;ll send a one-time reset link valid for 60 minutes.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-md space-y-6">
          <div>
            <div className="font-mono text-xs text-[#FF3B30] tracking-widest">ELMNTS / RECOVERY</div>
            <h1 className="font-display text-5xl tracking-tighter mt-2 text-black">RESET PASSWORD</h1>
          </div>

          {sent ? (
            <div className="space-y-4">
              <div className="bg-black/[0.04] border border-black/10 p-5 text-sm text-black/70 leading-relaxed" data-testid="forgot-success">
                If an account exists for <span className="font-mono text-black">{identifier}</span>, a password-reset link has been sent. It expires in 60 minutes and can be used once.
              </div>
              {devUrl && (
                <div className="bg-yellow-50 border border-yellow-300 p-4 text-xs font-mono break-all text-yellow-900" data-testid="dev-reset-url">
                  <div className="font-display tracking-widest text-[10px] mb-2">DEV-ONLY: RESET URL</div>
                  <a className="underline" href={devUrl}>{devUrl}</a>
                </div>
              )}
              <Link to="/login" className="block text-center font-display tracking-widest text-sm underline text-black hover:text-[#FF3B30]" data-testid="link-back-login">
                BACK TO LOGIN
              </Link>
            </div>
          ) : (
            <>
              <label className="block">
                <span className="font-display tracking-widest text-[10px] text-black/50">EMAIL OR MOBILE NUMBER</span>
                <input
                  type="text"
                  required
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="you@example.com or 9876543210"
                  className="w-full bg-transparent border-b border-black/30 focus:border-black outline-none py-3 text-black placeholder:text-black/30"
                  data-testid="forgot-identifier"
                />
              </label>
              {error && <p className="text-sm text-[#FF3B30]" data-testid="forgot-error">{error}</p>}
              <button
                type="submit"
                disabled={busy}
                className="w-full bg-black text-white py-4 font-display tracking-widest text-sm hover:bg-[#FF3B30] transition-colors disabled:opacity-60"
                data-testid="forgot-submit"
              >
                {busy ? "SENDING..." : "SEND RESET LINK"}
              </button>
              <p className="text-sm text-black/60 text-center">
                Remembered it? <Link to="/login" className="underline font-semibold text-black hover:text-[#FF3B30]" data-testid="link-login">BACK TO LOGIN</Link>
              </p>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
